/**
 * Every open page follows the newest deploy (the user, 2026-10-10: "all files
 * should work with versions — the phones should always get the new files").
 *
 * The files are versioned already — every chunk is named by its content hash
 * and the page is revalidated on every load — but a page that stays OPEN (the
 * phone app's web view lives in the background for days) never asks again.
 * So: when the page comes back to the front, every 10 minutes while it is in
 * front, and when the network returns, it fetches its own page with no cache
 * and compares the entry script's name with the one it is running. A new name
 * is a new deploy:
 *
 * - coming back to the front → reload at once (nothing has been touched yet);
 * - otherwise → reload the next time the page goes to the background.
 *
 * Never on the admin hosts (`radm.`, `appadm.`): a reload there could lose a
 * half-typed form; they still have `staleBuild.ts`'s reload-on-missing-chunk.
 * Never under the dev server (no hashed entry, nothing to compare).
 */
import { isConsoleHost, isRaveAdminHost } from './tenant';

const ENTRY = /\/assets\/index-[\w-]+\.js/;
const EVERY_MS = 10 * 60_000;

/** The entry script this page is running, e.g. "/assets/index-Cffep3pt.js". */
export function runningEntry(doc: Document = document): string | null {
  for (const s of doc.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const m = ENTRY.exec(s.getAttribute('src') ?? '');
    if (m) return m[0];
  }
  return null;
}

/** The entry the server's page names now, or null when it cannot be read. */
export async function latestEntry(fetcher: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetcher(`/?build-check=${Date.now()}`, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    return ENTRY.exec(await res.text())?.[0] ?? null;
  } catch {
    return null; // offline, or a blip — try again on the next trigger
  }
}

export function followNewBuild(opts: {
  running?: string | null; fetcher?: typeof fetch; reload?: () => void;
} = {}): () => void {
  const running = opts.running === undefined ? runningEntry() : opts.running;
  if (!running || isRaveAdminHost() || isConsoleHost()) return () => {};
  const reload = opts.reload ?? (() => window.location.reload());
  let stale = false;
  let busy = false;

  const check = async (justReturned: boolean) => {
    if (busy || stale) return;
    busy = true;
    const latest = await latestEntry(opts.fetcher);
    busy = false;
    if (!latest || latest === running) return;
    stale = true;
    if (justReturned || document.visibilityState === 'hidden') reload();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') { if (stale) reload(); return; }
    void check(true);
  };
  const onOnline = () => { void check(false); };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
  const timer = window.setInterval(() => { if (document.visibilityState === 'visible') void check(false); }, EVERY_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('online', onOnline);
    window.clearInterval(timer);
  };
}
